<template>
  <SogCard>
    <SogCardTitle>Kundenstatus</SogCardTitle>
    <SogCardContent v-show="!_editing">
      {{ _personStatus === statuses.vertriebspartner ? 'Vertriebspartner' : 'Endabnehmer' }}
    </SogCardContent>
    
    <SogCardContent v-show="_editing">
      <SogRadioButtonGroup
        v-if="!reloading"
        v-model="_editedPersonStatus"
        :name="_personStatus"
        :items="[
          {label: 'Endabnehmer', value: statuses.endabnehmer},
          {label: 'Vertriebspartner', value: statuses.vertriebspartner},
        ]"
      />
    </SogCardContent>
    
    <div v-show="_editedPersonStatus === statuses.vertriebspartner && _editing">
      <SogCardTitle>Vertriebsvereinbarung</SogCardTitle>
      <SogCardContent>
        <SogDatePicker
          v-model="_editedBeginDate"
          :max="_editedEndDate"
          placeholder="Beginn"
        />
  
        <SogDatePicker
          v-model="_editedEndDate"
          :min="_editedBeginDate"
          placeholder="Ende"
        />
      </SogCardContent>
    </div>

    <div v-show="_personStatus === statuses.vertriebspartner && !_editing">
      <SogCardTitle>Vertriebsvereinbarung</SogCardTitle>
      <SogCardContent>
        <table>
          <tr>
            <td>Beginn</td>
            <td>
              <SogDataLabel 
                v-if="_beginDate"
                :value="_beginDate"
                :value-type="SogDataLabelValueType.Date"
              />

              <center
                v-else
                class="w-100"
              >
                -
              </center>
            </td>
          </tr>
          <tr>
            <td>Ende</td>
            <td>
              <SogDataLabel
                v-if="_endDate"
                :value="_endDate"
                :value-type="SogDataLabelValueType.Date" 
              />
              <center
                v-else
                class="w-100"
              >
                -
              </center>
            </td>
          </tr>
        </table>
      </SogCardContent>
    </div>

    <SogCardActions v-show="!_editing">
      <SogCardActionButton 
        mdi-icon="mdi-pencil"
        @click="startEditing()"
      />
    </SogCardActions>

    <SogCardActions v-show="_editing">
      <SogCardActionButton 
        mdi-icon="mdi-content-save"
        @click="onStatusUpdate()"
      />
      <SogCardActionButton 
        mdi-icon="mdi-cancel"
        @click="cancel()"
      />
    </SogCardActions>
  </SogCard>
</template>

<script setup lang="ts">

import SogCardActionButton from '@/shared/ui/SogCardActionButton.vue';
import SogCardActions from '@/shared/ui/SogCardActions.vue';
import SogCardContent from '@/shared/ui/SogCardContent.vue';
import SogCardTitle from '@/shared/ui/SogCardTitle.vue';
import SogDatePicker from '@/shared/ui/SogDatePicker.vue';
import SogRadioButtonGroup from '@/shared/ui/SogRadioButtonGroup.vue';
import SogDataLabel from '@/shared/ui/SogDataLabel.vue';

import { ref, onMounted } from 'vue';
import { SogDataLabelValueType } from '@/shared/ui/types/SogDataLabelValueType';
import PersonDetails from '@/shared/model/personDetails';
import PersonDetailsId from '@/shared/model/personDetailsId';
import Person from "@/shared/model/person";
import SogCard from '@/shared/ui/SogCard.vue';

import apiOrganisationStructure from "@/modules/OrganisationStructure/api";
import {useAccountStore} from "@/stores/accountStore";

const props = defineProps<{
  details: PersonDetails,
  person: Person
}>()

const emit = defineEmits(['onSaveClick', 'onFinishEdit'])

enum statuses {
  endabnehmer = 'EndCustomer',
  vertriebspartner = 'Agent'
}

const _accountStore = useAccountStore()

const _personStatus = ref(props.details.getValue(PersonDetailsId.CustomerType))

const _agentFrom = props.details.getValue(PersonDetailsId.AgentFrom)
const _agentTill = props.details.getValue(PersonDetailsId.AgentTill)

const _beginDate = ref<Date | undefined>(_agentFrom != undefined ? new Date(props.details.getValue(PersonDetailsId.AgentFrom)) : undefined)
const _endDate = ref<Date | undefined>(_agentTill != undefined ? new Date(props.details.getValue(PersonDetailsId.AgentTill)) : undefined)

const _editedPersonStatus = ref(_personStatus.value)
const _editedBeginDate = ref<Date | undefined>(_beginDate.value)
const _editedEndDate = ref<Date | undefined>(_endDate.value)
const _editing = ref(false)


onMounted(() => {
  onStatusReset()
})

const reloading = ref(false)

function reload() {
  reloading.value = true
  setTimeout(() => {
    reloading.value = false
  }, 100)
}

async function onStatusUpdate() {
  emit('onSaveClick')
  try {
    const requestBody = getDetails().getValues()
    const res = await apiOrganisationStructure.updateDetails(_accountStore.person?.id, props.person?.id, requestBody)
  
    if(res) {
      emit('onFinishEdit', true)
      _editing.value = false
      _personStatus.value = _editedPersonStatus.value
      _beginDate.value = _editedBeginDate.value
      _endDate.value = _editedEndDate.value
    } else {
      emit('onFinishEdit', false)
    }
  } catch {
    emit('onFinishEdit', false)
  }
}

function startEditing() {
  _editing.value = true
}

function cancel() {
  _editing.value = false
  onStatusReset()
}

function onStatusReset() {
  _editedPersonStatus.value = _personStatus.value
  _editedBeginDate.value = _beginDate.value
  _editedEndDate.value = _endDate.value
  reload()
}

function getDetails() {
  const details = new PersonDetails(props.details?.getValues())
  details.setValue(PersonDetailsId.CustomerType, _editedPersonStatus.value)

  if(_editedPersonStatus.value === statuses.vertriebspartner) {
    details.setDateValue(PersonDetailsId.AgentFrom, _editedBeginDate.value as Date)
    details.setDateValue(PersonDetailsId.AgentTill, _editedEndDate.value as Date)
  }
  return details
}


</script>

<style scoped>

.w-100 {
  width: 70px;
}

</style>